import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Divider,
} from '@mui/material';
import fileShareApi from '../../services/fileShareApi';
import axios from 'axios';
import { filterConnectedConnectors, isConnectorConnected } from './fileShareConnectors';

const ProjectConnectors = ({ projectId, projectName, onConnectorUpdate, variant = 'page' }) => {
  const isDialog = variant === 'dialog';

  const [connectors, setConnectors] = useState({
    storageConnectorId: null,
    forgeConnectorId: null,
  });
  const [availableConnectors, setAvailableConnectors] = useState({
    storage: [],
    forge: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    fetchProjectConnectors();
    fetchAvailableConnectors();
  }, [projectId]);

  const fetchProjectConnectors = async () => {
    try {
      const response = await fileShareApi.get(`/projects/${projectId}/connectors`);
      setConnectors(response.data);
    } catch (err) {
      console.error('Failed to fetch project connectors:', err);
      setError('Failed to load project connectors');
    }
  };

  const fetchAvailableConnectors = async () => {
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenantId');

      const connectorTokensResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3002/'}synctrack/sync-connectors/tenant/${tenantId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const connectorTokens = connectorTokensResponse.data || [];

      if (connectorTokens.length === 0) {
        setAvailableConnectors({ storage: [], forge: [] });
        setLoading(false);
        return;
      }

      const masterConnectorsResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3002/'}synctrack/master-connector`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const masterConnectors = masterConnectorsResponse.data || [];

      const connectorsWithType = connectorTokens.map((token) => {
        const masterConnector = masterConnectors.find(
          (mc) =>
            mc.mcId?.toString() === token.mcId?.toString() ||
            mc.mcRecId?.toString() === token.mcId?.toString()
        );

        return {
          id: token.id,
          mcId: token.mcId,
          connectorName:
            (token.description || '').trim() ||
            (masterConnector ? masterConnector.mcName : `Unknown (ID: ${token.mcId})`),
          mcName: masterConnector?.mcName || null,
          connectorType: masterConnector ? masterConnector.mcConnectorType : null,
          status: token.status,
          ...token,
        };
      });

      const storageConnectors = connectorsWithType.filter((connector) => {
        const type = connector.connectorType;
        return (
          type &&
          (type === 'STORAGE' ||
            type === 'MINIO_STORAGE' ||
            type === 'AWS_S3_STORAGE' ||
            type === 'EGNYTE_STORAGE' ||
            type === 'GOOGLE_DRIVE_STORAGE' ||
            type === 'SHAREPOINT_STORAGE' ||
            type === 'DROPBOX_STORAGE')
        );
      });

      const translationConnectors = connectorsWithType.filter((connector) => {
        const type = connector.connectorType;
        return (
          type &&
          (type === 'AUTODESK' ||
            type === 'FORGE' ||
            type === 'AUTODESK_FORGE' ||
            type.toLowerCase() === 'autodesk' ||
            type.toLowerCase() === 'forge')
        );
      });

      setAvailableConnectors({
        storage: storageConnectors,
        forge: translationConnectors,
      });
    } catch (err) {
      console.error('Failed to fetch available connectors:', err);
      setAvailableConnectors({ storage: [], forge: [] });
    } finally {
      setLoading(false);
    }
  };

  const handleConnectorChange = async (connectorType, connectorId) => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const endpoint = `/projects/${projectId}/${connectorType}-connector`;
      const payload = {
        [`${connectorType}ConnectorId`]: connectorId || null,
      };

      await fileShareApi.put(endpoint, payload);

      setConnectors((prev) => ({
        ...prev,
        [`${connectorType}ConnectorId`]: connectorId,
      }));

      setSuccess(
        `${connectorType.charAt(0).toUpperCase() + connectorType.slice(1)} connector updated successfully`
      );

      if (onConnectorUpdate) {
        onConnectorUpdate(connectorType, connectorId);
      }
    } catch (err) {
      console.error(`Failed to update ${connectorType} connector:`, err);
      setError(`Failed to update ${connectorType} connector`);
    } finally {
      setSaving(false);
    }
  };

  const getConnectorName = (connectorId, connectorList) => {
    const connector = connectorList.find(
      (c) => c.id?.toString() === connectorId?.toString() || c.mcId?.toString() === connectorId?.toString()
    );
    return connector ? connector.connectorName : 'Unknown Connector';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  const storageOptions = filterConnectedConnectors(
    availableConnectors.storage,
    connectors.storageConnectorId
  );
  const forgeOptions = filterConnectedConnectors(
    availableConnectors.forge,
    connectors.forgeConnectorId
  );

  const content = (
    <Box sx={{ width: '100%' }}>
      {!isDialog && (
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Project Connectors — {projectName}
        </Typography>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Storage Connector */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
          Storage Connector
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose a Connected storage provider (Egnyte, MinIO, AWS S3, etc.) for this project.
        </Typography>

        <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
          <InputLabel id="storage-connector-label">Storage Provider</InputLabel>
          <Select
            labelId="storage-connector-label"
            label="Storage Provider"
            value={connectors.storageConnectorId || ''}
            onChange={(e) => handleConnectorChange('storage', e.target.value)}
            disabled={saving}
          >
            <MenuItem value="">
              <em>Use Default Storage</em>
            </MenuItem>
            {storageOptions.map((connector) => (
              <MenuItem key={connector.id} value={connector.id}>
                {connector.connectorName}
                {!isConnectorConnected(connector.status) && connector.status
                  ? ` (${connector.status})`
                  : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {storageOptions.length === 0 && (
          <Alert severity="info" sx={{ mb: 1 }}>
            No Connected storage connectors. Connect one under Connector Token first.
          </Alert>
        )}

        {connectors.storageConnectorId && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            <Chip
              label={getConnectorName(connectors.storageConnectorId, availableConnectors.storage)}
              color="primary"
              variant="outlined"
              size="small"
            />
          </Box>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Translation Connector */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
          Translation Connector (Forge / Autodesk)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose Connected translation credentials for CAD file translation.
        </Typography>

        <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
          <InputLabel id="forge-connector-label">Translation Credentials</InputLabel>
          <Select
            labelId="forge-connector-label"
            label="Translation Credentials"
            value={connectors.forgeConnectorId || ''}
            onChange={(e) => handleConnectorChange('forge', e.target.value)}
            disabled={saving}
          >
            <MenuItem value="">
              <em>Use Default Translation Credentials</em>
            </MenuItem>
            {forgeOptions.map((connector) => (
              <MenuItem key={connector.id} value={connector.id}>
                {connector.connectorName}
                {!isConnectorConnected(connector.status) && connector.status
                  ? ` (${connector.status})`
                  : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {forgeOptions.length === 0 && (
          <Alert severity="info" sx={{ mb: 1 }}>
            No Connected Autodesk/Forge connectors. Connect one under Connector Token first.
          </Alert>
        )}

        {connectors.forgeConnectorId && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            <Chip
              label={getConnectorName(connectors.forgeConnectorId, availableConnectors.forge)}
              color="primary"
              variant="outlined"
              size="small"
            />
          </Box>
        )}
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 2, lineHeight: 1.5 }}>
        <strong>Tip:</strong> Connect connectors under Connector Token first (status Connected), then
        assign them here.
      </Typography>
    </Box>
  );

  if (isDialog) {
    return content;
  }

  return (
    <Card>
      <CardContent sx={{ p: 3 }}>{content}</CardContent>
    </Card>
  );
};

export default ProjectConnectors;
